/**
 * Use file specific import paths instead of modules (e.g. @/interfaces/services)
 * so we don't automatically import a ton of other stuff.
 *
 * Changing this might break the showroom build because .gql are
 * included and we currently don't use the GraphQL rollup plugin for t.
 */
import { Segment } from '@/plugins/segment';
import { ProductionLoggingService } from '@/services/logging/production';
import { StagingLoggingService } from '@/services/logging/staging';
import { IS_PROD_ENV, IS_STAGING_ENV } from '@/utils/config';
import { useFeatureToggles } from '@/utils/featureToggles';

import { ConsoleLoggingService } from './console';
import { LoggingService } from './types';

let loggingRegistered = false;
let loggingService: LoggingService = new ConsoleLoggingService();

export function registerLoggingServiceComposable(): {
  loggingService: LoggingService;
  logger: Segment | null;
  loggingRequiresSentry: boolean;
} {
  const { ENABLE_PRODUCTION_LOGGING_ON_DEV, ENABLE_STAGING_LOGGING_ON_DEV } = useFeatureToggles();

  const useProductionTracking = IS_PROD_ENV || ENABLE_PRODUCTION_LOGGING_ON_DEV;
  const useStagingTracking = IS_STAGING_ENV || ENABLE_STAGING_LOGGING_ON_DEV;
  let logger: Segment | null = null;

  if (useProductionTracking) {
    logger = new Segment();
    loggingService = new ProductionLoggingService(logger);
  } else if (useStagingTracking) {
    loggingService = new StagingLoggingService();
  } else {
    loggingService = new ConsoleLoggingService();
  }

  loggingRegistered = true;

  return {
    loggingService,
    logger,
    loggingRequiresSentry: useProductionTracking || useStagingTracking,
  };
}

export function useLoggingService(): LoggingService {
  if (!loggingRegistered)
    // eslint-disable-next-line no-console
    console.error(
      new Error(
        'Logging service is not registered. Please call registerLoggingServiceComposable() before using it.',
      ),
    );
  return loggingService;
}
